/**
 * tileCache.js - Tile caching system for WorldMapPanel
 *
 * Handles:
 * - Tile signature generation (change detection)
 * - Cache validation (destroyed element detection)
 * - Cache cleanup (removed tiles)
 */

/**
 * Generate a unique signature for a tile based on its visual properties
 * @param {Object} tileState - Tile state properties
 * @returns {string} Signature string for change detection
 */
export function generateTileSignature(tileState) {
  const {
    fillColor,
    borderColor,
    borderWidth,
    placementFadeAlpha,
    isCurrentNode,
    hasOutpost,
    hasTileBuilding,
    hasTradeRouteSegment,
    isValidBuildingPlacement,
    isInBuildingPlacementMode,
    isOnTradeRoute,
    tradeRouteOverlay,
    isUnderConstruction,
    hasCompletedGreatWork,
    isPartOfFullyRevealedWonderSite,
  } = tileState;

  return `${fillColor}-${borderColor}-${borderWidth}-${placementFadeAlpha}-${isCurrentNode}-${hasOutpost}-${hasTileBuilding}-${hasTradeRouteSegment}-${isValidBuildingPlacement && isInBuildingPlacementMode}-${isOnTradeRoute && tradeRouteOverlay}-${isUnderConstruction}-${hasCompletedGreatWork}-${isPartOfFullyRevealedWonderSite}`;
}

/**
 * Validate and clean up tile cache
 * Removes tiles that no longer exist in the world
 * NOTE: Does NOT remove tiles with destroyed elements - those are handled by isCacheValid()
 * This prevents cache deletion during tab switches when browser destroys graphics contexts
 * @param {Map} tileCache - Map of nodeId -> cachedTile
 * @param {Set} currentNodeIds - Set of currently discovered node IDs
 * @returns {number} Number of tiles removed
 */
export function cleanupTileCache(tileCache, currentNodeIds) {
  let removedCount = 0;

  for (const [nodeId, cachedTile] of tileCache.entries()) {
    // ONLY remove if tile no longer exists in the world
    // Don't remove if elements are destroyed - that's handled by cache invalidation
    if (!currentNodeIds.has(nodeId)) {
      // Two-phase cleanup: remove from parent first, then destroy
      if (cachedTile.elements) {
        // Phase 1: Remove all from parent
        cachedTile.elements.forEach(el => {
          try {
            if (el && el.parent) {
              el.parent.removeChild(el);
            }
          } catch (err) {
            // Ignore errors during removal
          }
        });
        // Phase 2: Destroy all elements
        cachedTile.elements.forEach(el => {
          try {
            if (el && !el.destroyed) {
              el.destroy();
            }
          } catch (err) {
            // Ignore errors during destruction
          }
        });
      }
      tileCache.delete(nodeId);
      removedCount++;
    }
  }

  return removedCount;
}

/**
 * Validate and clean up border cache
 * @param {Map} borderCache - Map of nodeId -> borderGraphics
 * @param {Set} currentNodeIds - Set of currently discovered node IDs
 * @returns {number} Number of borders removed
 */
export function cleanupBorderCache(borderCache, currentNodeIds) {
  let removedCount = 0;

  for (const [nodeId, border] of borderCache.entries()) {
    if (!currentNodeIds.has(nodeId) || (border && border.destroyed)) {
      try {
        // Phase 1: Remove from parent first
        if (border && border.parent) {
          border.parent.removeChild(border);
        }
      } catch (err) {
        // Ignore errors during removal
      }

      try {
        // Phase 2: Destroy after removal
        if (border && !border.destroyed) {
          border.destroy();
        }
      } catch (err) {
        // Ignore errors during destruction
      }

      borderCache.delete(nodeId);
      removedCount++;
    }
  }

  return removedCount;
}

/**
 * Check if a cached tile is still valid (signature matches and no destroyed/orphaned elements)
 * This is the ONLY place that checks for destroyed elements - cleanup doesn't delete them
 * @param {Object} cachedTile - Cached tile data
 * @param {string} currentSignature - Current signature to compare
 * @param {PIXI.Container} expectedLayer - Optional layer to check parent validity
 * @returns {boolean} True if cache is valid
 */
export function isCacheValid(cachedTile, currentSignature, expectedLayer = null) {
  if (!cachedTile) return false;
  if (cachedTile.signature !== currentSignature) return false;

  // Check for destroyed or orphaned elements (e.g., from component remounting)
  // If found, cache is invalid and tile will be rebuilt
  if (cachedTile.elements && cachedTile.elements.length > 0) {
    const hasInvalidElements = cachedTile.elements.some(el => {
      if (!el) return false;
      if (el.destroyed) return true;
      // If expectedLayer is provided, check if element has an orphaned parent
      // (parent exists but is not the expected layer and not a child of the stage)
      if (expectedLayer && el.parent && el.parent !== expectedLayer) {
        // Element has a stale parent - likely from before remount
        // Check if the parent is still connected to the stage
        let current = el.parent;
        while (current && current.parent) {
          current = current.parent;
        }
        // If we can't find a root or root has no renderer, element is orphaned
        if (!current || current.destroyed) return true;
      }
      return false;
    });
    return !hasInvalidElements;
  }

  return true;
}

/**
 * Store tile in cache
 * @param {Map} tileCache - Tile cache map
 * @param {string} nodeId - Node ID
 * @param {string} signature - Tile signature
 * @param {Array} elements - Array of PIXI Graphics objects
 * @param {Array|null} fadeElements - Array of elements that can fade (for hover effects)
 */
export function storeTileInCache(tileCache, nodeId, signature, elements, fadeElements = null) {
  // Clean up old cached elements before storing new ones
  const oldCache = tileCache.get(nodeId);
  if (oldCache?.elements) {
    oldCache.elements.forEach(el => {
      try {
        if (el && el.parent) {
          el.parent.removeChild(el);
        }
      } catch (err) {
        // Ignore errors
      }
      try {
        if (el && !el.destroyed) {
          el.destroy();
        }
      } catch (err) {
        // Ignore errors
      }
    });
  }

  // Store new cache entry
  tileCache.set(nodeId, {
    signature,
    elements,
    fadeElements: fadeElements && fadeElements.length > 0 ? fadeElements : null,
  });
}

/**
 * Retrieve cached tile elements and re-add to layer
 * @param {Object} cachedTile - Cached tile data
 * @param {PIXI.Container} layer - Layer to add elements to
 * @returns {Object} Cached tile data (for fade element tracking)
 */
export function reuseCachedTile(cachedTile, layer) {
  // PERF: Only add elements if they're not already in the layer
  // This prevents duplicate graphics when useEffect re-runs on tile discovery
  cachedTile.elements.forEach(el => {
    if (el.parent !== layer) {
      layer.addChild(el);
    }
  });
  return cachedTile;
}

/**
 * Remove invalid cached elements from the layer
 * Called when cache is invalid but elements still exist in layer
 * @param {Object} cachedTile - Cached tile data with invalid/destroyed elements
 */
export function removeInvalidCachedElements(cachedTile) {
  if (!cachedTile?.elements) return;

  // Two-phase removal: remove from parent first, then destroy
  cachedTile.elements.forEach(el => {
    try {
      if (el && el.parent) {
        el.parent.removeChild(el);
      }
    } catch (err) {
      // Ignore errors during removal
    }
  });

  cachedTile.elements.forEach(el => {
    try {
      if (el && !el.destroyed) {
        el.destroy();
      }
    } catch (err) {
      // Ignore errors during destruction
    }
  });
}
